import React, { useState, useMemo } from 'react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Icon,
  IconButton,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

type Categoria = {
  codMidia: number;
  dscMidia: string;
  ativo: boolean;
};

interface TabelaTipoMidiaProps {
  listaTipoMidia: Categoria[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof Categoria; direcao: 'asc' | 'desc' } | null;

const TabelaTipoMidia = ({ listaTipoMidia, listaVazia, loading }: TabelaTipoMidiaProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Categoria | null>(null);

  const manipularOrdenacao = (chave: keyof Categoria) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaTipoMidia];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaTipoMidia, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Categoria) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderRows = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((tipoMidia) => (
        <Tr key={tipoMidia.codMidia}>
          <Td>{tipoMidia.codMidia}</Td>
          <Td>{tipoMidia.dscMidia}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-tipo-midia/${tipoMidia.codMidia}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={5} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('codMidia')}
              onMouseEnter={() => setColunaDestacada('codMidia')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={ordenacao?.chave === 'codMidia' ? 'blue.500' : 'inherit'}
              >
                Código{obterIndicadorOrdenacao('codMidia')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscMidia')}
              onMouseEnter={() => setColunaDestacada('dscMidia')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box
                display="flex"
                alignItems="center"
                color={ordenacao?.chave === 'dscMidia' ? 'blue.500' : 'inherit'}
              >
                Descrição{obterIndicadorOrdenacao('dscMidia')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={3}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderRows()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaTipoMidia;
